import * as React from "react"
import { Check, ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"

/*
 * Select, rebuilt on Popover + Command (cmdk) so EVERY dropdown has a search
 * box. Same exports/props as the old Radix Select wrapper.
 *
 * SelectValue must show the selected item's rendered children even while the
 * menu is closed, but Popover content only mounts when open. So SelectContent
 * ALWAYS renders its children: when closed the items render nothing visible
 * (they only register value -> label in context); when open they render as
 * cmdk items inside the popover.
 */

const SelectContext = React.createContext(null)

function useSelectContext() {
  const ctx = React.useContext(SelectContext)
  if (!ctx) throw new Error("Select components must be used inside <Select>")
  return ctx
}

function useControllable(controlled, defaultValue, onChange) {
  const [inner, setInner] = React.useState(defaultValue)
  const isControlled = controlled !== undefined
  const current = isControlled ? controlled : inner
  const set = React.useCallback(
    (next) => {
      if (!isControlled) setInner(next)
      if (onChange) onChange(next)
    },
    [isControlled, onChange]
  )
  return [current, set]
}

// Plain-text content of a React node, used for filtering and labels.
function nodeText(node) {
  if (node == null || typeof node === "boolean") return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(nodeText).join("")
  if (React.isValidElement(node)) return nodeText(node.props.children)
  return ""
}

const Select = ({
  value: valueProp,
  defaultValue,
  onValueChange,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  disabled,
  name,
  children,
}) => {
  const [value, setValue] = useControllable(valueProp, defaultValue, onValueChange)
  const [open, setOpen] = useControllable(openProp, defaultOpen, onOpenChange)
  const [labels, setLabels] = React.useState({})

  const register = React.useCallback((val, text, node) => {
    setLabels((prev) =>
      prev[val] && prev[val].text === text ? prev : { ...prev, [val]: { text, node } }
    )
    return () =>
      setLabels((prev) => {
        if (!(val in prev)) return prev
        const { [val]: _removed, ...rest } = prev
        return rest
      })
  }, [])

  const ctx = React.useMemo(
    () => ({ value, setValue, open, setOpen, labels, register, disabled }),
    [value, setValue, open, setOpen, labels, register, disabled]
  )

  return (
    <SelectContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={(o) => { if (!disabled || !o) setOpen(o) }}>
        {children}
      </Popover>
      {name ? <input type="hidden" name={name} value={value ?? ""} disabled={disabled} /> : null}
    </SelectContext.Provider>
  )
}

const SelectGroup = ({ children, className, ...props }) => {
  const { open } = useSelectContext()
  if (!open) return <>{children}</>
  return (
    <CommandGroup className={cn("p-0", className)} {...props}>
      {children}
    </CommandGroup>
  )
}

const SelectValue = ({ placeholder, children, className, ...props }) => {
  const { value, labels } = useSelectContext()
  const hasValue = value !== undefined && value !== null && value !== ""
  const entry = hasValue ? labels[value] : undefined
  return (
    <span className={cn("pointer-events-none", className)} {...props}>
      {children !== undefined && hasValue
        ? children
        : hasValue
          ? (entry ? entry.node : null)
          : placeholder}
    </span>
  )
}

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => {
  const { value, disabled } = useSelectContext()
  const hasValue = value !== undefined && value !== null && value !== ""
  return (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        type="button"
        role="combobox"
        disabled={disabled}
        data-placeholder={hasValue ? undefined : ""}
        className={cn(
          "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
          className
        )}
        {...props}>
        {children}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    </PopoverTrigger>
  )
})
SelectTrigger.displayName = "SelectTrigger"

// No-ops: the list is a scrollable cmdk list, kept for API compatibility.
const SelectScrollUpButton = () => null
SelectScrollUpButton.displayName = "SelectScrollUpButton"
const SelectScrollDownButton = () => null
SelectScrollDownButton.displayName = "SelectScrollDownButton"

// Substring match on the item's text (carried in keywords[0]); the cmdk
// `value` is a unique id so same-label items never collide.
const textFilter = (_value, search, keywords) => {
  const text = (keywords && keywords[0]) || ""
  return text.toLowerCase().includes(search.trim().toLowerCase()) ? 1 : 0
}

const SelectContent = React.forwardRef(
  // position / align etc. are accepted for API compatibility.
  ({ className, children, align = "start", sideOffset = 4, position: _position, style, ...props }, ref) => {
    const { open } = useSelectContext()
    const inputRef = React.useRef(null)

    if (!open) return <>{children}</>

    return (
      <PopoverContent
        ref={ref}
        side="bottom"
        avoidCollisions={false}
        align={align}
        sideOffset={sideOffset}
        style={{ maxHeight: "var(--radix-popover-content-available-height)", ...style }}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
        onWheel={(e) => e.stopPropagation()}
        className={cn("flex flex-col w-auto min-w-[var(--radix-popover-trigger-width)] p-0", className)}
        {...props}>
        <Command filter={textFilter} className="min-h-0 flex-1">
          <CommandInput ref={inputRef} placeholder="Search…" autoFocus className="h-9" />
          <CommandList className="min-h-0 flex-1">
            <CommandEmpty>No results.</CommandEmpty>
            {children}
          </CommandList>
        </Command>
      </PopoverContent>
    )
  }
)
SelectContent.displayName = "SelectContent"

const SelectLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSelectContext()
  if (!open) return null
  return <div ref={ref} className={cn("px-2 py-1.5 text-sm font-semibold", className)} {...props} />
})
SelectLabel.displayName = "SelectLabel"

const SelectItem = React.forwardRef(
  ({ className, children, value, disabled, textValue, ...props }, ref) => {
    const { value: selected, setValue, setOpen, open, register } = useSelectContext()
    const id = React.useId()
    const text = textValue ?? nodeText(children)
    const childrenRef = React.useRef(children)
    childrenRef.current = children

    // Register value -> label so SelectValue can render it while closed.
    React.useLayoutEffect(
      () => register(value, text, childrenRef.current),
      [register, value, text]
    )

    if (!open) return null

    const isSelected = selected === value
    return (
      <CommandItem
        ref={ref}
        value={id}
        keywords={[text]}
        disabled={disabled}
        onSelect={() => {
          setValue(value)
          setOpen(false)
        }}
        className={cn(
          "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none",
          className
        )}
        {...props}>
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          {isSelected ? <Check className="h-4 w-4" /> : null}
        </span>
        <span>{children}</span>
      </CommandItem>
    )
  }
)
SelectItem.displayName = "SelectItem"

const SelectSeparator = React.forwardRef(({ className, ...props }, ref) => {
  const { open } = useSelectContext()
  if (!open) return null
  return <CommandSeparator ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
})
SelectSeparator.displayName = "SelectSeparator"

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
